// app/keeptrack/camera/camera-state.ts
import { SatMath } from '@app/app/analysis/sat-math';
import { ServiceLocator } from '@app/engine/core/service-locator';
import { alt2zoom } from '@app/engine/utils/transforms';
import { keepTrackApi } from '@app/keepTrackApi';
import { SelectSatManager } from '@app/plugins/select-sat-manager/select-sat-manager';
/**
 * Centralized state management for the camera system.
 * Groups related properties logically while keeping them in a single class.
 */
export class CameraState {
    // ============ Core Transform ============
    position = [0, 0, 0];
    camPitch = 0;
    camYaw = 0;
    // ============ Zoom ============
    zoomLevel_ = 0.6925;
    zoomTarget_ = 0.6925;
    camDistBuffer_ = 0;
    earthCenteredLastZoom = 0.6925;
    isZoomIn = false;
    // ============ Rotation Speeds & Targets ============
    camPitchTarget = 0;
    camPitchSpeed = 0;
    camYawTarget = 0;
    camYawSpeed = 0;
    camRotateSpeed = 0;
    // ============ Earth-Centered Mode ============
    earthCenteredPitch_ = 0;
    earthCenteredYaw_ = 0;
    // ============ Fixed-to-Satellite Mode ============
    ftsPitch = 0;
    ftsYaw = 0;
    ftsRotateReset = true;
    /**
     * This was used when there was only one camera mode and the camera was always centered on the earth
     * It is the overall yaw of the camera?
     */
    camZoomSnappedOnSat = false;
    camAngleSnappedOnSat = false;
    /**
     * This was used when there was only one camera mode and the camera was always centered on the earth
     * It is the overall pitch of the camera?
     */
    camSnapToSat = {
        pos: { x: 0, y: 0, z: 0 },
        radius: 0,
        pitch: 0,
        yaw: 0,
        altitude: 0,
        camDistTarget: 0,
    };
    // ============ FPS Mode ============
    fpsPos_ = [0, 25000, 0];
    fpsPitch = 0;
    fpsYaw = 0;
    fpsRotate = 0;
    fpsPitchRate = 0;
    fpsYawRate = 0;
    fpsRotateRate = 0;
    fpsForwardSpeed = 0;
    fpsSideSpeed = 0;
    fpsVertSpeed = 0;
    fpsRun = 1;
    isFPSForwardSpeedLock_ = false;
    isFPSSideSpeedLock_ = false;
    isFPSVertSpeedLock_ = false;
    // ============ Local Rotation ============
    isLocalRotateOverride = false;
    isLocalRotateReset = true;
    isLocalRotateRoll = false;
    isLocalRotateYaw = false;
    localRotateTarget = {
        pitch: 0,
        roll: 0,
        yaw: 0,
    };
    localRotateCurrent = {
        pitch: 0,
        roll: 0,
        yaw: 0,
    };
    localRotateDif = {
        pitch: 0,
        roll: 0,
        yaw: 0,
    };
    localRotateSpeed = {
        pitch: 0,
        roll: 0,
        yaw: 0,
    };
    localRotateStartPosition = {
        pitch: 0,
        roll: 0,
        yaw: 0,
    };
    // ============ Panning ============
    isPanReset = false;
    isScreenPan = false;
    isWorldPan = false;
    panCurrent = { x: 0, y: 0, z: 0 };
    panSpeed = { x: 0, y: 0, z: 0 };
    panStartPosition = { x: 0, y: 0, z: 0 };
    panTarget_ = { x: 0, y: 0, z: 0 };
    panDif_ = { x: 0, y: 0, z: 0 };
    // ============ User Interaction ============
    isDragging = false;
    dragStartPitch = 0;
    dragStartYaw = 0;
    screenDragPoint = [0, 0];
    mouseX = 0;
    mouseY = 0;
    startMouseX = 0;
    startMouseY = 0;
    speedModifier = 1;
    isHoldingDownAKey = 1;
    // ============ Auto Behaviors ============
    isAutoPitchYawToTarget = false;
    isAutoRotate_ = true;
    // ============ Getters/Setters ============
    get zoomLevel() {
        return this.zoomLevel_;
    }
    set zoomLevel(val) {
        this.zoomLevel_ = Math.max(0.0001, Math.min(1, val));
    }
    get zoomTarget() {
        return this.zoomTarget_;
    }
    set zoomTarget(val) {
        this.zoomTarget_ = Math.max(0.01, Math.min(1, val));
        this.zoomTargetChange();
    }
    get camDistBuffer() {
        return this.camDistBuffer_;
    }
    set camDistBuffer(val) {
        this.camDistBuffer_ = Math.max(val, settingsManager.minDistanceFromSatellite);
    }
    get earthCenteredPitch() {
        return this.earthCenteredPitch_;
    }
    set earthCenteredPitch(val) {
        this.earthCenteredPitch_ = val;
    }
    get earthCenteredYaw() {
        return this.earthCenteredYaw_;
    }
    set earthCenteredYaw(val) {
        this.earthCenteredYaw_ = val;
    }
    get fpsPos() {
        return this.fpsPos_;
    }
    set fpsPos(val) {
        this.fpsPos_ = val;
    }
    get isFPSForwardSpeedLock() {
        return this.isFPSForwardSpeedLock_;
    }
    set isFPSForwardSpeedLock(val) {
        this.isFPSForwardSpeedLock_ = val;
    }
    get isFPSSideSpeedLock() {
        return this.isFPSSideSpeedLock_;
    }
    set isFPSSideSpeedLock(val) {
        this.isFPSSideSpeedLock_ = val;
    }
    get isFPSVertSpeedLock() {
        return this.isFPSVertSpeedLock_;
    }
    set isFPSVertSpeedLock(val) {
        this.isFPSVertSpeedLock_ = val;
    }
    get isAutoRotate() {
        return this.isAutoRotate_;
    }
    set isAutoRotate(val) {
        this.isAutoRotate_ = val;
    }
    get panTarget() {
        return this.panTarget_;
    }
    get panDif() {
        return this.panDif_;
    }
    // ============ Methods ============
    zoomTargetChange() {
        const selectSatManagerInstance = keepTrackApi.getPlugin(SelectSatManager);
        const maxCovarianceDistance = Math.min((selectSatManagerInstance?.primarySatCovMatrix?.[2] ?? 0) * 10, 10000);
        if ((settingsManager.isZoomStopsSnappedOnSat || (selectSatManagerInstance?.selectedSat ?? -1) === -1) || (this.camDistBuffer >= settingsManager.nearZoomLevel)) {
            settingsManager.selectedColor = settingsManager.selectedColorFallback;
            keepTrackApi.getRenderer().setFarRenderer();
            this.earthCenteredLastZoom = this.zoomTarget;
            this.camZoomSnappedOnSat = false;
            // calculate camera distance from target
            const target = selectSatManagerInstance?.getSelectedSat();
            if (target) {
                const satAlt = SatMath.getAlt(target.position, SatMath.calculateTimeVariables(ServiceLocator.getTimeManager().simulationTimeObj).gmst);
                const curMinZoomLevel = alt2zoom(satAlt, settingsManager.minZoomDistance, settingsManager.maxZoomDistance, settingsManager.minDistanceFromSatellite);
                if (this.zoomTarget < this.zoomLevel && this.zoomTarget < curMinZoomLevel) {
                    this.camZoomSnappedOnSat = true;
                    if (settingsManager.isDrawCovarianceEllipsoid) {
                        this.camDistBuffer = (Math.max(Math.max(this.camDistBuffer, settingsManager.nearZoomLevel), maxCovarianceDistance) - 1);
                    }
                    else {
                        this.camDistBuffer =
                            Math.min(Math.max(this.camDistBuffer, settingsManager.nearZoomLevel), settingsManager.minDistanceFromSatellite);
                    }
                }
            }
        }
        else {
            keepTrackApi.getRenderer().setNearRenderer();
            // Clamping camDistBuffer to be between minDistanceFromSatellite and maxZoomDistance
            this.camDistBuffer = Math.min(Math.max(this.camDistBuffer, settingsManager.minDistanceFromSatellite), Math.max(settingsManager.nearZoomLevel, maxCovarianceDistance));
        }
    }
    /**
     * Reset camera state to defaults
     * @param isHardReset - If true, also resets zoom level
     */
    reset(isHardReset = false) {
        // Reset zoom
        if (isHardReset) {
            this.zoomLevel_ = 0.6925;
        }
        this.zoomTarget_ = 0.6925;
        this.isZoomIn = false;
        this.camZoomSnappedOnSat = false;
        this.camAngleSnappedOnSat = false;
        this.camDistBuffer = 0;
        // Reset position
        this.position = [0, 0, 0];
        this.fpsPos_ = [0, 25000, 0];
        // Reset rotation
        this.camPitch = 0;
        this.camPitchTarget = 0;
        this.camPitchSpeed = 0;
        this.camYaw = 0;
        this.camYawTarget = 0;
        this.camYawSpeed = 0;
        this.camRotateSpeed = 0;
        // Reset FPS state
        this.fpsPitch = 0;
        this.fpsYaw = 0;
        this.fpsRotate = 0;
        this.fpsPitchRate = 0;
        this.fpsYawRate = 0;
        this.fpsRotateRate = 0;
        this.fpsForwardSpeed = 0;
        this.fpsSideSpeed = 0;
        this.fpsVertSpeed = 0;
        this.fpsRun = 1;
        this.isFPSForwardSpeedLock_ = false;
        this.isFPSSideSpeedLock_ = false;
        this.isFPSVertSpeedLock_ = false;
        // Reset local rotation
        this.isLocalRotateReset = !!isHardReset;
        this.isLocalRotateOverride = false;
        this.isLocalRotateRoll = false;
        this.isLocalRotateYaw = false;
        this.localRotateTarget = { pitch: 0, roll: 0, yaw: 0 };
        this.localRotateCurrent = { pitch: 0, roll: 0, yaw: 0 };
        this.localRotateDif = { pitch: 0, roll: 0, yaw: 0 };
        this.localRotateSpeed = { pitch: 0, roll: 0, yaw: 0 };
        this.localRotateStartPosition = { pitch: 0, roll: 0, yaw: 0 };
        // Reset pan
        this.isPanReset = false;
        this.isScreenPan = false;
        this.isWorldPan = false;
        this.panCurrent = { x: 0, y: 0, z: 0 };
        this.panSpeed = { x: 0, y: 0, z: 0 };
        this.panStartPosition = { x: 0, y: 0, z: 0 };
        this.panTarget_ = { x: 0, y: 0, z: 0 };
        this.panDif_ = { x: 0, y: 0, z: 0 };
        // Reset interaction
        this.isDragging = false;
        this.screenDragPoint = [0, 0];
        this.dragStartPitch = 0;
        this.dragStartYaw = 0;
        this.mouseX = 0;
        this.mouseY = 0;
        this.startMouseX = 0;
        this.startMouseY = 0;
        this.speedModifier = 1;
        this.isHoldingDownAKey = 1;
        this.isAutoPitchYawToTarget = !!isHardReset;
    }
    /**
     * Reset only rotation-related state
     */
    resetRotation() {
        this.isLocalRotateReset = true;
        this.ftsRotateReset = true;
    }
    resetPan() {
        if (this.isPanReset) {
            this.panTarget.x = 0;
            this.panTarget.y = 0;
            this.panTarget.z = 0;
            this.panDif.x = -this.panCurrent.x;
            this.panDif.y = this.panCurrent.y;
            this.panDif.z = this.panCurrent.z;
        }
    }
    /**
     * Clone the current state (useful for debugging or snapshots)
     */
    clone() {
        const cloned = new CameraState();
        // Deep copy all properties
        Object.assign(cloned, JSON.parse(JSON.stringify(this)));
        // Restore vec3 arrays that got serialized
        cloned.position = [...this.position];
        cloned.fpsPos_ = [...this.fpsPos_];
        return cloned;
    }
}
